/**
 * Action Guard — Phase 2b
 *
 * Client-side classification of proposed Copilot actions.
 * Every write/destructive action must pass through here before execution.
 * Read actions are auto-allowed without confirmation.
 */

import type { UiAction } from './action-bridge'

// ── Types ──────────────────────────────────────────────────────────────────────

export type ActionClass = 'read' | 'write' | 'destructive' | 'outward'

export interface ActionGuardResult {
  allowed: boolean
  requiresConfirm: boolean
  actionClass: ActionClass
  reason?: string
  piiTouched?: boolean
}

/**
 * Minimal shape for backend tool calls that the guard also classifies.
 */
export interface BackendToolCall {
  op: string
  tool_name?: string
  side_effect?: 'read' | 'write' | 'destructive' | 'outward'
  form_id?: string
  field_names?: string[]
  [key: string]: unknown
}

// ── PII keyword detector ───────────────────────────────────────────────────────

const PII_KEYWORDS = ['pia', 'consent', 'dsr', 'personal', 'pii', 'transfer']

function hasPiiTouched(formId?: string, fieldNames?: string[]): boolean {
  const targets = [formId ?? '', ...(fieldNames ?? [])].map((s) =>
    s.toLowerCase(),
  )
  return targets.some((t) => PII_KEYWORDS.some((kw) => t.includes(kw)))
}

// ── Session opt-in store ───────────────────────────────────────────────────────

const SESSION_KEY_PREFIX = 'sgrc-copilot-allow-'

/**
 * Returns true if the user has checked "always allow" for this action class
 * in the current browser session (stored in sessionStorage).
 */
export function isSessionAllowed(actionClass: ActionClass): boolean {
  try {
    return sessionStorage.getItem(`${SESSION_KEY_PREFIX}${actionClass}`) === 'true'
  } catch {
    return false
  }
}

/**
 * Persist "always allow" for an action class for the rest of this session.
 */
export function setSessionAllow(actionClass: ActionClass, allow: boolean) {
  try {
    if (allow) {
      sessionStorage.setItem(`${SESSION_KEY_PREFIX}${actionClass}`, 'true')
    } else {
      sessionStorage.removeItem(`${SESSION_KEY_PREFIX}${actionClass}`)
    }
  } catch {
    // sessionStorage unavailable — silently skip
  }
}

// ── Core classifier ────────────────────────────────────────────────────────────

/**
 * Classify a UiAction or BackendToolCall and decide whether it requires
 * human confirmation before execution.
 *
 * Rules:
 *   read ops  → allowed immediately, no confirm required
 *   write ops → allowed, but always show HITL gate (unless session opt-in)
 *   destructive/outward → confirm always, log violation
 */
export function classifyAction(
  action: UiAction | BackendToolCall,
): ActionGuardResult {
  const op = action.op

  // ── Read ops — navigate, highlight, readContext, scrollTo, openTab ──────────
  if (
    op === 'navigate' ||
    op === 'highlight' ||
    op === 'readContext' ||
    op === 'scrollTo' ||
    op === 'openTab'
  ) {
    return {
      allowed: true,
      requiresConfirm: false,
      actionClass: 'read',
    }
  }

  // ── Write ops — fillForm, setField ──────────────────────────────────────────
  if (op === 'fillForm' || op === 'setField') {
    const formId = (action as BackendToolCall).form_id as string | undefined
    const fieldNames = op === 'setField'
      ? [(action as BackendToolCall).field as string]
      : Object.keys((action as BackendToolCall).values ?? {})
    const piiTouched = hasPiiTouched(formId, fieldNames)

    // Session opt-in bypasses confirm
    if (isSessionAllowed('write')) {
      return {
        allowed: true,
        requiresConfirm: false,
        actionClass: 'write',
        piiTouched,
      }
    }

    return {
      allowed: true,
      requiresConfirm: true,
      actionClass: 'write',
      piiTouched,
    }
  }

  // ── Write op — submitForm ────────────────────────────────────────────────────
  if (op === 'submitForm') {
    const formId = (action as BackendToolCall).form_id as string | undefined
    const piiTouched = hasPiiTouched(formId)

    if (isSessionAllowed('write')) {
      return {
        allowed: true,
        requiresConfirm: false,
        actionClass: 'write',
        piiTouched,
      }
    }

    return {
      allowed: true,
      requiresConfirm: true,
      actionClass: 'write',
      reason: 'La soumission d\'un formulaire requiert confirmation.',
      piiTouched,
    }
  }

  // ── Destructive ops ──────────────────────────────────────────────────────────
  if (op === 'deleteRecord') {
    void logActionGuardViolation(action, 'destructive')
    return {
      allowed: true,
      requiresConfirm: true,
      actionClass: 'destructive',
      reason: 'La suppression est irréversible.',
      piiTouched: false,
    }
  }

  // ── Outward ops ──────────────────────────────────────────────────────────────
  if (
    op === 'publishContent' ||
    op === 'sendEmail' ||
    op === 'submitToExternalSystem'
  ) {
    void logActionGuardViolation(action, 'outward')
    return {
      allowed: true,
      requiresConfirm: true,
      actionClass: 'outward',
      reason: 'Cette action est irréversible et affecte des systèmes externes.',
      piiTouched: false,
    }
  }

  // ── Backend tool call with explicit side_effect ───────────────────────────────
  const bc = action as BackendToolCall
  if (bc.side_effect === 'read') {
    return { allowed: true, requiresConfirm: false, actionClass: 'read' }
  }
  if (bc.side_effect === 'write') {
    if (isSessionAllowed('write')) {
      return { allowed: true, requiresConfirm: false, actionClass: 'write' }
    }
    return { allowed: true, requiresConfirm: true, actionClass: 'write' }
  }
  if (bc.side_effect === 'destructive') {
    void logActionGuardViolation(action, 'destructive')
    return {
      allowed: true,
      requiresConfirm: true,
      actionClass: 'destructive',
      reason: 'Action destructive — confirmation obligatoire.',
    }
  }
  if (bc.side_effect === 'outward') {
    void logActionGuardViolation(action, 'outward')
    return {
      allowed: true,
      requiresConfirm: true,
      actionClass: 'outward',
      reason: 'Action sortante — confirmation obligatoire.',
    }
  }

  // ── Unknown op — conservative: require confirm ────────────────────────────────
  return {
    allowed: true,
    requiresConfirm: true,
    actionClass: 'write',
    reason: `Opération inconnue "${op}" — confirmation requise par prudence.`,
  }
}

// ── Violation logger (best-effort, fire-and-forget) ────────────────────────────

async function logActionGuardViolation(
  action: UiAction | BackendToolCall,
  actionClass: 'destructive' | 'outward',
): Promise<void> {
  try {
    await fetch('/api/v1/ai-ia-pro/action-guard/classify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action_type: action.op,
        form_id: (action as BackendToolCall).form_id,
        field_names: (action as BackendToolCall).field_names,
        side_effect: actionClass,
      }),
    })
  } catch {
    // Non-blocking — guard operates client-side even if server unreachable
  }
}
